import React, { useEffect, useRef } from 'react';
import { SmileOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { messageModel } from '../MessageModel';
import { MessageInputToolbarExtras } from './MessagePlusPanel';
import { EmojiPanel } from './EmojiPanel';
import { insertTextAtCursor } from '../EmojiTextUtil';

interface ChatInputBarProps {
  roomId: number;
}

export const ChatInputBar: React.FC<ChatInputBarProps> = observer(
  ({ roomId }) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const canSend =
      messageModel.inputText.trim().length > 0 && !messageModel.sending;

    useEffect(() => {
      if (!messageModel.emojiPanelVisible) {
        return;
      }
      const onPointerDown = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest('.message-emoji-panel')) {
          return;
        }
        if (target.closest('[data-emoji-toggle="true"]')) {
          return;
        }
        messageModel.closeEmojiPanel();
      };
      document.addEventListener('mousedown', onPointerDown);
      return () => document.removeEventListener('mousedown', onPointerDown);
    }, [messageModel.emojiPanelVisible]);

    const insertEmoji = (id: string) => {
      const tag = `[${id}]`;
      const textarea = textareaRef.current;
      if (!textarea) {
        messageModel.inputText += tag;
        return;
      }
      const { next, cursor } = insertTextAtCursor(
        messageModel.inputText,
        tag,
        textarea.selectionStart,
        textarea.selectionEnd,
      );
      messageModel.inputText = next;
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(cursor, cursor);
      });
    };

    const send = () => {
      messageModel.closeEmojiPanel();
      void messageModel.sendText(roomId);
    };

    return (
      <div className="message-input-area">
        {messageModel.emojiPanelVisible && (
          <div className="message-emoji-panel-wrap">
            <EmojiPanel visible onSelect={insertEmoji} />
          </div>
        )}
        <div className="message-input-box">
          <div
            className="message-input-editor"
            onClick={() => textareaRef.current?.focus()}
          >
            <textarea
              ref={textareaRef}
              className="message-input-textarea"
              value={messageModel.inputText}
              rows={5}
              onChange={(e) => {
                messageModel.inputText = e.target.value;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
          </div>
          <div className="message-input-toolbar">
            <div className="message-input-toolbar-left">
              <button
                type="button"
                data-emoji-toggle="true"
                className={`message-input-tool-btn${messageModel.emojiPanelVisible ? ' active' : ''}`}
                title="表情"
                onClick={() => messageModel.toggleEmojiPanel()}
              >
                <SmileOutlined />
              </button>
              <MessageInputToolbarExtras roomId={roomId} />
            </div>
            <button
              type="button"
              className="message-input-send"
              disabled={!canSend}
              onClick={send}
            >
              发送
            </button>
          </div>
        </div>
      </div>
    );
  },
);
